import { useTheme } from '@simplywallst/ui-core';
import { useTranslation } from '@/hooks/useTranslation';
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const events = {
  financial: 'chart05',
  strategy: 'chart04',
  dividend: 'good',
  other: 'chart01',
  management: 'chart02',
} as const;

type Category = 'dividend' | 'financial' | 'management' | 'strategy' | 'other';

export const filters = [
  'dividend',
  'financial',
  'management',
  'strategy',
  'other',
] as Category[];

export const getCategoryColor = (category: Category) => {
  if (category in events) {
    return events[category];
  }
  return 'white';
};

const ChartKeyWrapper = styled.button<{
  canClick: boolean;
  hasIcon: boolean;
  isDisabled: boolean;
}>`
  cursor: ${({ canClick }) => (canClick ? 'pointer' : 'default')};
  padding: 4px 10px 4px ${({ hasIcon }) => (hasIcon ? 8 : 10)}px;
  margin-left: 2px;
  margin-bottom: 2px;
  font-size: ${({ theme }) => theme.x1space + 0.4}rem;
  color: ${({ isDisabled }) => (isDisabled ? '#4A5663' : 'white')};
  text-transform: capitalize;
  svg {
    margin-right: ${({ theme }) => theme.x1spacePx / 2}px;
  }
`;

const defaultProps = {
  icon: '',
  fill: '',
  label: '',
  disabled: false,
  handleClick: null,
};

const ChartKey = ({ icon, fill, label, disabled, handleClick }) => {
  return (
    <ChartKeyWrapper
      data-cy-id={`event-key-color-${fill}`}
      onClick={handleClick}
      isDisabled={disabled}
      canClick={handleClick !== null}
      hasIcon={icon !== void 0 && icon.length > 0}
    >
      {icon && (
        <svg
          width={12}
          height={12}
          viewBox="0 0 11 11"
          xmlns="http://www.w3.org/2000/svg"
        >
          {icon === 'circle' && !disabled && (
            <circle
              cx="50%"
              cy="60%"
              r="3.7"
              stroke="transparent"
              strokeWidth="2"
              fill={fill}
            />
          )}
          {icon === 'circle' && disabled && (
            <circle
              cx="50%"
              cy="60%"
              r="3.7"
              stroke="#4A5663"
              fillOpacity="0"
              strokeWidth="1.5"
            />
          )}
        </svg>
      )}
      <span>{label}</span>
    </ChartKeyWrapper>
  );
};

ChartKey.defaultProps = defaultProps;
ChartKey.propTypes = {
  icon: PropTypes.string,
  fill: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  handleClick: PropTypes.func,
};

interface PriceChartKeyProps {
  icon: string;
  fill: string;
  label: string;
  disabled: boolean;
  handleClick: (label: string) => void;
}

class PriceChartKey extends PureComponent<PriceChartKeyProps> {
  handleClick = () => {
    const { handleClick, label } = this.props;
    handleClick(label);
  };

  render() {
    const { handleClick } = this;
    const { fill, icon, label, disabled } = this.props;
    return (
      <ChartKey
        fill={fill}
        icon={icon}
        label={label}
        disabled={disabled}
        handleClick={handleClick}
      />
    );
  }
}

interface Props {
  toggledFilters: Category[];
  onUpdate: (filters: Category[]) => void;
}

function PriceChartLegend({ onUpdate, toggledFilters }: Props) {
  const theme = useTheme();
  const t = useTranslation();
  return (
    <div>
      {filters.map((filter) => {
        return (
          <PriceChartKey
            key={`event-key-${filter}`}
            fill={theme.color[getCategoryColor(filter)]}
            icon="circle"
            label={t(`app.component.priceChart.${filter}`)}
            disabled={!toggledFilters.includes(filter)}
            handleClick={() => {
              const nextToggle = toggledFilters.includes(filter)
                ? toggledFilters.filter((current) => current !== filter)
                : toggledFilters.concat(filter);
              /*
              sort abc before with dispatch
              this is purely for react-query to be key friendly
              */
              nextToggle.sort((a, b) => {
                if (a < b) return -1;
                if (a > b) return 1;
                return 0;
              });
              onUpdate(nextToggle);
            }}
          />
        );
      })}
    </div>
  );
}

export default PriceChartLegend;
